/* import __COLOCATED_TEMPLATE__ from './admin-blaster-info-and-filters.hbs'; */
import { readOnly, alias, equal } from '@ember/object/computed';
import { A } from '@ember/array';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import Component from '@ember/component';
import { ASSIGNMENT } from "../../../models/assignment";
import {
  ADMIN_BLASTER__DISTANCES_AVAILABLE,
  ADMIN_BLASTER__STAR_FILTER_COUNT
} from "../../../constants/routes/admin/blaster";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

/**
 * This is the main information component for the admin blaster tool that will show the info about the posting as well
 * as the available filters
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: 'div',
  classNames: ['SH-c-admin-blaster-info-and-filters'],

  /**
   * Register services
   */
  errorTracker: service('error-tracker'),
  locationCommandService : service('commands/location-commands'),
  state: service('states/routes/admin/blaster'),
  stateManager: service('state-managers/routes/admin/blaster'),
  store: service(),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /*********************************
   * PRIVATE
   **********************************/

  tempSelectedLocationName: '',

  /**
   * This is the options for the multi-select for selection for just all countries.
   *
   * e.g.
   Ember.A([{
      title: 'France',
      value: '12'
    },{
      title: 'USA',
      value: '2'
    }])
   *
   * @type {Ember.MutableArray}
   */
  _allCountryOptions: computed('state.availableCountries', function() {
    const countryOptions = [];
    const countryOptionsMap = {};

    this.state.availableCountries.forEach((country) => {
      const option = {
        title: country.name,
        value: country.id,
      };

      countryOptions.addObject(option);
      countryOptionsMap[country.id] = option;
    });

    countryOptions.sort((countryA, countryB) => countryA.title.localeCompare(countryB.title));

    this.set('_allCountryOptionsMap', countryOptionsMap);
    return countryOptions;
  }),

  _equipmentOptions: computed('state.availableEquipment.@each.{name,id}', function() {
    const equipmentOptions = this.state.availableEquipment.map(equipment => (
      {
        title: equipment.name,
        value: equipment.id,
      }
    ));

    equipmentOptions.sort((equipmentA, equipmentB) => equipmentA.title > equipmentB.title ? 1 : -1);
    return equipmentOptions;
  }),

  _languagesOptions: computed('state.availableLanguages.@each.{name,code}', function() {
    const languageOptions = this.state.availableLanguages.map(language => (
      {
        title: language.name,
        value: language.code,
      }
    ));

    languageOptions.sort((languageA, languageB) => languageA.title > languageB.title ? 1 : -1);
    return languageOptions;
  }),

  /**
   * This is a map indexed by country id of all the country options for the multi-select.
   *
   * e.g. {
   *  4: {
        title: 'USA',
        value: '4'
      }
      6: {
        title: 'France',
        value: '6'
      }
   * }
   *
   * @type {Object}
   */
  _allCountryOptionsMap: {},

  /**
   * @type {Boolean}    True when the form elements should be disabled
   */
  _areFormElementsDisabled: readOnly('state.areFormElementsDisabled'),

  /**
   * @type {Ember.A<Blast>}     All blasts associated with this assignment.
   */
  _blasts: readOnly('state.currentAssignment.blasts'),

  /**
   * This is the options for the multi-select for selection for the possible blast distances
   *
   * @see ADMIN_BLASTER__DISTANCES_AVAILABLE
   *
   * e.g.
   Ember.A([{
      title: 'France',
      value: '12',
      selected: bool
    },{
      title: 'USA',
      value: '2',
      selected: bool
    }])
   *
   * @type {Ember.MutableArray}
   */
  _blastDistanceOptions: computed('_updatedBlastDistance', function() {
    let blastDistanceOptions = A(),
      currentBlastDistance = this.get('_updatedBlastDistance');

    ADMIN_BLASTER__DISTANCES_AVAILABLE.forEach(blastDistance => {
      blastDistanceOptions.pushObject({
        title: blastDistance + 'KM',
        value: blastDistance,
        selected: blastDistance === currentBlastDistance
      });
    });

    return blastDistanceOptions;
  }),

  /**
   * NOTE -- ALIASED TO TOP LEVEL STATE
   *
   * @type {String}     Optional custom message to send along with the blast.
   */
  _customMessage: alias('state.customMessage'),

  /**
   * This is the grouped options for the multi-select for selection region or country.
   *
   Ember.A([{
    groupName: 'region',
    groupTitle: 'Regions of Yore',
    options: Ember.A([{
      title: 'Europe',
      value: '3'
    },{
      title: 'North America',
      value: '4'
    }])
  },{
    groupName: 'country',
    groupTitle: 'Countries of Justic',
    options: Ember.A([{
      title: 'France',
      value: '12'
    },{
      title: 'USA',
      value: '2'
    }])
  }])
   *
   * @type {Ember.MutableArray}
   */
  _multiSelectRegionsCountriesOptions: computed('_allCountryOptions', 'state.availableRegions.length', function() {
    let regionsCountriesOptions = A(),
      countryOptions = this.get('_allCountryOptions'),
      regionOptions = A();

    this.get('state.availableRegions').forEach((region) => {
      regionOptions.addObject({
        title: region.get('name'),
        value: region.get('id')
      });
    });

    regionsCountriesOptions.addObject({
      groupName: 'region',
      groupTitle: 'Regions',
      options: regionOptions
    });

    regionsCountriesOptions.addObject({
      groupName: 'country',
      groupTitle: 'Countries',
      options: countryOptions
    });

    return regionsCountriesOptions;
  }),

  /**
   * @type {Assignment}   The current posting in the blaster view.
   */
  _posting: readOnly('state.currentAssignment'),

  /**
   * @type {String}       Human readable text for the rate type.
   */
  _rateTypeText: computed('_posting.rateType', function() {
    return this.get('_posting.rateType') === ASSIGNMENT.RATE_TYPE.FLAT ? 'Flat Rate' : 'Day Rate';
  }),

  /**
   * @type {Ember.A<Object>}   Menu options for the asset.
   */
  _selectStatusOptions: computed(
    '_posting.select',
    function() {
      let options = A();

      options.pushObject({
        title: "Doesn't matter",
        value: null
      });

      options.pushObject({
        title: 'Select Only',
        value: '1'
      });

      options.pushObject({
        title: 'Non-select Only',
        value: '0'
      });

      return options;
    }
  ),

  /**
   * Array of objects for the min rating star buttons for the freelancer.
   *
   * e.g.
   Ember.A([{
      title: 'France',
      value: '12'
      selected: bool
    },{
      title: 'USA',
      value: '2',
      selected: bool
    }])
   *
   * @type {Ember.A<Object>}
   */
  _starButtons: computed('state.updatedSelectedMinRating', function() {
    let starButtonObjects = A(),
      selectedMinRating = this.get('state.updatedSelectedMinRating');

    for (let i = 0; i <= ADMIN_BLASTER__STAR_FILTER_COUNT; i++) {
      starButtonObjects.pushObject({
        title: i + '+',
        value: i,
        selected: selectedMinRating === i
      });
    }

    return starButtonObjects;
  }),

  /**
   * @type {Number}     The blast distance the admin has selected.
   */
  _updatedBlastDistance: readOnly('state.updatedBlastDistance'),

  /**
   * @type {Location|null}    The city Location model selected by the user or null
   */
  _updatedCityLocation: readOnly('state.updatedCityLocation'),

  /**
   * If we have a city location, get the text for it.
   *
   * @private
   * @type {String}
   */
  _updatedCityLocationText: computed(
    '_updatedCityLocation.{isGlobal,formattedAddress}',
    'tempSelectedLocationName',
    function() {
      if (this._updatedCityLocation?.isGlobal) {
        return '';
      }

      return this.tempSelectedLocationName
        || this._updatedCityLocation?.formattedAddress
        || '';
  }),

  /**
   * @type {Ember.MutableArray<Object>}   Array of the option items that have the country names and ids.
   */
  _updatedCountryOptions: readOnly('state.updatedCountryOptions'),

  _updatedEquipmentOptions: readOnly('state.updatedEquipmentOptions'),

  _updatedLanguageOptions: readOnly('state.updatedLanguageOptions'),

  /**
   * The min rating
   * @type {Number}
   */
  _updatedSelectedMinRating: readOnly('state.updatedSelectedMinRating'),

  /**
   * @type {Number}
   */
  _updatedSelectStatus: readOnly('state.updatedSelectStatus'),

  /**
   * @type {Ember.A<Object>}      @see state.updatedServiceOptions
   */
  _updatedServiceOptions: readOnly('state.updatedServiceOptions'),

  /**
   * Use raw location data to set a location model in the state
   *
   * @param {Location} locationRecord Location record that has not been saved.
   */
  setLocationFromGoogleRecord: task(function *(locationRecord) {
    if (!locationRecord?.place_id) {
      // we didn't get a proper place from google.  Can happen when clicking enter on existing place or clear field
      this.stateManager.updateCityLocation(null);
      this.set('tempSelectedLocationName', '');
      return;
    }

    // use the raw data to set a temp label to the location
    this.set('tempSelectedLocationName', locationRecord?.formatted_address ?? '');

    try {
      const location = yield this.locationCommandService.createLocationFromLocationRecord(locationRecord);
      this.stateManager.updateCityLocation(location);
    } catch (e) {
      this.stateManager.updateCityLocation(null);
    } finally {
      this.set('tempSelectedLocationName', '');
    }
  }),

  actions: {
    /**
     * When the user clicks the link to clear selected countries.
     */
    onClearAllCountriesClicked: function() {
      this.get('stateManager').updateSelectedCountries(A(), true);
    },

    onClearAllEquipmentClicked() {
      this.stateManager.clearEquipment();
    },

    onClearAllLanguagesClicked() {
      this.stateManager.clearLanguages();
    },

    onCountryOptionRemoved: function(countryOption) {
      this.stateManager.removeSelectedCountry(countryOption);
    },

    /**
     * @param {Object} countryRegionOption    Option selected on country/region click
     */
    onCountryRegionOptionSelected: function(countryRegionOption) {
      let countryOptionsMap = this.get('_allCountryOptionsMap'),
        selectedOptions = A();

      // update the selected countries based on what region we're in.
      if (countryRegionOption.groupName === 'region') {
        let region = this.get('store').peekRecord('region', countryRegionOption.value),
          regionCountries = region.get('countries');

        regionCountries.forEach((country) => {
          selectedOptions.push(countryOptionsMap[country.get('id')]);
        });

        this.get('stateManager').updateSelectedCountries(selectedOptions, true);
        return;
      }

      this.get('stateManager').updateSelectedCountries(A([countryOptionsMap[countryRegionOption.value]]));
    },

    onEquipmentOptionRemoved(equipmentOption) {
      this.stateManager.removeSelectedEquipment(equipmentOption);
    },

    onEquipmentOptionSelected(equipmentOption) {
      this.stateManager.addSelectedEquipment(equipmentOption);
    },

    onLanguageOptionRemoved(languageOption) {
      this.stateManager.removeSelectedLanguage(languageOption);
    },

    onLanguageOptionSelected(languageOption) {
      this.stateManager.addSelectedLanguage(languageOption);
    },

    /**
     * When the user clicks the link to clear selected countries.
     * @param {Boolean} currentIsCountryRestricted
     */
    onCountryRestrictedClicked(currentIsCountryRestricted) {
      this.stateManager.updateCountryRestricted(!currentIsCountryRestricted);
    },

    /**
     * Search for the blast results after a click by the admin.
     */
    onSearchBlastResults: function() {
      this.get('stateManager').searchBlastResults().catch(e => {
        handleErrorResponseWithModal(this, e);
      });
    },

    onSearchTypeToggleClicked(targetSearchType) {
      this.stateManager.updateServiceSearchType(targetSearchType);
    },

    /**
     * Update the blast distance as selected by the admin.
     *
     * @param {Number} blastDistance    of type ADMIN_BLASTER__DISTANCES_AVAILABLE
     */
    onSelectBlastDistance: function(blastDistance) {
      this.get('stateManager').updateBlastDistance(blastDistance);
    },

    /**
     * Called when the user chooses a select status
     * @param {Number} selectStatus
     */
    onSelectStatusChanged: function(selectStatus) {
      this.get('stateManager').updateSelectStatus(parseInt(selectStatus));
    },

    /**
     * Called when admin selects the min rating for the blast.
     *
     * @param {Number} ratingSelected
     */
    onSelectStarAmountClicked: function(ratingSelected) {
      this.get('stateManager').updateSelectedMinRating(ratingSelected);
    },

    /**
     * Called when the admin clicks on one of the service ids.
     *
     * @param {String} serviceId
     * @param {Boolean} isSelected
     */
    onServiceClicked: function(serviceId, isSelected) {
      this.get('stateManager').updateToggleServiceSelected(serviceId, isSelected);
    }
  }
});
